import Cookies from 'universal-cookie'
import add from 'date-fns/add'

const cookies = new Cookies()

export const AUTH_RETURN_TO_KEY = 'auth:return-to'
export const AUTH_END_REASON_KEY = 'auth:end-reason'
export const AUTH_FAILURE_DIAGNOSTIC_KEY = 'auth:last-failure'
export const AUTH_LOGOUT_EVENT_KEY = 'auth:logout-event'
export const AUTH_SESSION_EXTENDED_EVENT_KEY = 'auth:session-extended-event'
export const AUTH_EXPIRY_KEY = 'auth:hr-expires-at'

const SESSION_ENDED_REASON = 'session-ended'
const LEGACY_HR_COOKIE_NAME = 'hr_access_token'
const PRO_COOKIE_NAME = 'access_token'

let isHandlingUnauthorized = false

const browserHostname = () =>
  typeof window !== 'undefined' ? window.location.hostname : ''

export const getHrCookieName = () => {
  if (process.env.REACT_APP_HR_COOKIE_NAME) {
    return process.env.REACT_APP_HR_COOKIE_NAME
  }

  return browserHostname().startsWith('staging-')
    ? 'staging_hr_access_token'
    : LEGACY_HR_COOKIE_NAME
}

const configuredCookieDomain = () => process.env.REACT_APP_HR_DOMAIN || undefined

const cookieOptions = (expires) => {
  const options = { path: '/' }
  const domain = configuredCookieDomain()

  if (domain) options.domain = domain
  if (expires) options.expires = expires

  return options
}

const removeCookieEverywhere = (name) => {
  const hostname = browserHostname()
  const domains = [undefined, configuredCookieDomain(), '.adnexio.jobs', hostname]

  Array.from(new Set(domains)).forEach((domain) => {
    const options = { path: '/' }
    if (domain) options.domain = domain
    cookies.remove(name, options)
  })
}

const removeHostScopedCookie = (name) => {
  const hostname = browserHostname()
  const domains = [undefined, hostname]

  Array.from(new Set(domains)).forEach((domain) => {
    const options = { path: '/' }
    if (domain) options.domain = domain
    cookies.remove(name, options)
  })
}

export const getHrAccessToken = () => cookies.get(getHrCookieName())

export const getProAccessToken = () => cookies.get(PRO_COOKIE_NAME)

export const hasHrAccessToken = () => Boolean(getHrAccessToken())

export const getKnownHrExpiry = () => sessionStorage.getItem(AUTH_EXPIRY_KEY)

export const setAuthenticationCookies = ({ hrAccessToken, proAccessToken, hrExpiresAt }) => {
  const fallbackExpiry = add(new Date(), { days: 30 })
  const parsedExpiry = hrExpiresAt ? new Date(hrExpiresAt) : fallbackExpiry
  const hrExpiry = Number.isNaN(parsedExpiry.getTime()) ? fallbackExpiry : parsedExpiry

  if (hrAccessToken) {
    cookies.set(getHrCookieName(), hrAccessToken, cookieOptions(hrExpiry))
    if (hrExpiresAt) sessionStorage.setItem(AUTH_EXPIRY_KEY, hrExpiry.toISOString())
  }

  if (proAccessToken) {
    cookies.set(PRO_COOKIE_NAME, proAccessToken, cookieOptions(fallbackExpiry))
  }
}

export const clearAuthenticationCookies = () => {
  const currentHrCookieName = getHrCookieName()
  removeCookieEverywhere(currentHrCookieName)

  // On staging, the parent-domain legacy name is production's active cookie.
  // Only remove host-scoped legacy leftovers there so a staging logout cannot
  // terminate a production browser session.
  if (currentHrCookieName !== LEGACY_HR_COOKIE_NAME) {
    removeHostScopedCookie(LEGACY_HR_COOKIE_NAME)
  }

  removeCookieEverywhere(PRO_COOKIE_NAME)
  sessionStorage.removeItem(AUTH_EXPIRY_KEY)
}

export const isSafeInternalReturnTo = (value) =>
  typeof value === 'string' &&
  value.startsWith('/') &&
  !value.startsWith('//') &&
  !value.startsWith('/login')

export const getCurrentInternalLocation = () => {
  if (typeof window === 'undefined') return '/app/homepage'

  return `${window.location.pathname}${window.location.search}${window.location.hash}`
}

export const consumeReturnTo = (fallback = '/app/homepage') => {
  const stored = sessionStorage.getItem(AUTH_RETURN_TO_KEY)
  sessionStorage.removeItem(AUTH_RETURN_TO_KEY)
  return isSafeInternalReturnTo(stored) ? stored : fallback
}

export const consumeSessionEndReason = () => {
  const reason = sessionStorage.getItem(AUTH_END_REASON_KEY)
  sessionStorage.removeItem(AUTH_END_REASON_KEY)
  return reason
}

const notifyOtherTabs = (reason) => {
  try {
    localStorage.setItem(
      AUTH_LOGOUT_EVENT_KEY,
      JSON.stringify({ reason, occurred_at: new Date().toISOString() })
    )
  } catch (error) {
    // Storage can be unavailable in private browsing. The current tab can still log out.
  }
}

export const notifySessionExtended = (expiresAt) => {
  try {
    localStorage.setItem(
      AUTH_SESSION_EXTENDED_EVENT_KEY,
      JSON.stringify({ expires_at: expiresAt, occurred_at: new Date().toISOString() })
    )
  } catch (error) {
    // The shared cookie is still updated for this tab when storage is unavailable.
  }
}

export const clearLocalSession = ({ reason, notify = true } = {}) => {
  clearAuthenticationCookies()

  if (reason) sessionStorage.setItem(AUTH_END_REASON_KEY, reason)
  if (notify) notifyOtherTabs(reason || 'logout')
}

export const isPublicAuthenticationRequest = (url = '') => {
  const path = String(url).split('?')[0]
  return (
    path.endsWith('/login') ||
    path.includes('/pre-signup/') ||
    path.includes('/signup/')
  )
}

export const shouldEndSessionForResponse = (status) => status === 401

export const endSession = ({
  requestPath = '',
  authorizationPresent = false,
  message = 'Unauthenticated request',
  reason = SESSION_ENDED_REASON,
} = {}) => {
  if (isHandlingUnauthorized) return
  isHandlingUnauthorized = true

  const currentLocation = getCurrentInternalLocation()
  if (isSafeInternalReturnTo(currentLocation)) {
    sessionStorage.setItem(AUTH_RETURN_TO_KEY, currentLocation)
  }

  sessionStorage.setItem(AUTH_END_REASON_KEY, reason)
  sessionStorage.setItem(
    AUTH_FAILURE_DIAGNOSTIC_KEY,
    JSON.stringify({
      occurred_at: new Date().toISOString(),
      request_path: String(requestPath).split('?')[0],
      authorization_present: authorizationPresent,
      message,
    })
  )

  clearLocalSession({ reason })

  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.replace('/login')
  }
}

export const handleUnauthorized = (error) => {
  const requestUrl = error?.config?.url || ''

  if (
    isHandlingUnauthorized ||
    error?.config?.skipAuthSessionHandling ||
    isPublicAuthenticationRequest(requestUrl)
  ) {
    return
  }

  endSession({
    requestPath: requestUrl,
    authorizationPresent: Boolean(
      error?.config?.headers?.Authorization || error?.config?.headers?.authorization
    ),
    message: error?.response?.data?.message || 'Unauthenticated request',
  })
}

export const resetUnauthorizedHandling = () => {
  isHandlingUnauthorized = false
}

export const subscribeToSessionLogout = (callback) => {
  if (typeof window === 'undefined') return () => {}

  const listener = (event) => {
    if (event.key !== AUTH_LOGOUT_EVENT_KEY || !event.newValue) return

    try {
      callback(JSON.parse(event.newValue))
    } catch (error) {
      callback({ reason: SESSION_ENDED_REASON })
    }
  }

  window.addEventListener('storage', listener)
  return () => window.removeEventListener('storage', listener)
}

export const subscribeToSessionExtended = (callback) => {
  if (typeof window === 'undefined') return () => {}

  const listener = (event) => {
    if (event.key !== AUTH_SESSION_EXTENDED_EVENT_KEY || !event.newValue) return

    try {
      callback(JSON.parse(event.newValue))
    } catch (error) {
      // A malformed event cannot change the current session.
    }
  }

  window.addEventListener('storage', listener)
  return () => window.removeEventListener('storage', listener)
}

export { SESSION_ENDED_REASON }
